import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { downloadRequestSchema } from "@/lib/downloads.shared";
import type { DownloadRequestResult } from "@/lib/downloads.shared";

export const requestGatedDownload = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => ({ parsed: downloadRequestSchema.safeParse(input) }))
  .handler(async ({ data }): Promise<DownloadRequestResult> => {
    if (!data.parsed.success) {
      return { ok: false, message: "Please enter a valid business email address." };
    }
    const input = data.parsed.data;
    const forwardedFor = getRequestHeader("x-forwarded-for") ?? "";
    const clientIp = forwardedFor.split(",")[0]?.trim() || getRequestHeader("cf-connecting-ip") || "";

    const { guardForm, hashIp } = await import("@/lib/form-guard.server");
    const verdict = guardForm({
      origin: getRequestHeader("origin"),
      referer: getRequestHeader("referer"),
      honeypot: input.company,
      startedAt: input.startedAt,
      minFillMs: 1_500,
      checkContent: false,
      keys: [
        ...(clientIp ? [{ key: `dl:${hashIp(clientIp)}`, max: 8 }] : []),
        { key: `dl:mail:${input.email.toLowerCase()}`, max: 6 },
      ],
    });
    if (!verdict.ok) {
      console.warn("[downloads] blocked:", verdict.kind);
      const { recordGuardBlock, identifyRequestUser } = await import("@/lib/guard-log.server");
      const actor = await identifyRequestUser(getRequestHeader("authorization"));
      await recordGuardBlock({
        source: "downloads",
        kind: verdict.kind,
        ip: clientIp,
        path: input.sourcePath ?? null,
        detail: input.slug,
        userId: actor.userId,
        userEmail: actor.email,
      });
      return {
        ok: false,
        message:
          verdict.kind === "rate_limited"
            ? "You have requested several downloads already. Please wait a few minutes."
            : "Please try again.",
      };
    }

    const { handleDownloadRequest } = await import("@/lib/downloads.server");
    return handleDownloadRequest(input, clientIp);
  });
